import { AfterViewInit, Component, ElementRef, Input, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { MhsControl } from '../shared/model/ui/mhs-control';
import { Questions } from '../shared/model/ui/questions'; 
import { UserSessionService } from '../shared/service/user-session.service';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { EEOI_APP_ROUTES, GlobalConstants } from '../shared/global-constants';
import { FormUtils } from '../shared/helper/form-utils';
import { CmElement, CmHelp, ContentType } from '../shared/model/cm/managed-content.model';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { MhsService } from '../shared/service/mhs.service';
import { LevelTwoQuestion } from '../shared/model/ui/level-two-question';
import { AdditionalQuestion } from '../shared/model/ui/additional-question';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AppInsightsApiService } from '../shared/service/app-insights-api.service';
import { of, Subscription } from 'rxjs';
import { WindowRef } from '../shared/service/window.service';
import { ModalService } from '../shared/service/modal.service';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { ApplicantType } from '../shared/model/ui/applicant-type';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CoverageMhs } from '../shared/model/session/coverage-mhs';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';
import { AdditionalDetailsComponent } from '../additional-details/additional-details.component';
import { MhsCheckboxComponent } from '../mhs-checkbox/mhs-checkbox.component';
import { ApplicationService } from '../shared/service/application.service';
import { PartialSaveResp } from '../shared/model/partial-save-resp';
import { PartialSaveData } from '../shared/model/partial-save-data';
import { environment } from '@environment';

export class RenamedControl{
  originalName: string;
  newName: string;
}

export class IndexCompare{
  originalIndex: string;
  newIndex: string;
}

@Component({
  selector: 'app-med-history',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent, AdditionalDetailsComponent, MhsCheckboxComponent],
  templateUrl: './med-history.component.html',
  styleUrls: ['./med-history.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MedHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEmitter = new EventEmitter<void>();
  @Output() backEmitter = new EventEmitter<void>();
  @Output() val = new EventEmitter<boolean>();
  @Input() parentForm: UntypedFormGroup;
  @Input() showBackButton: boolean = false;

  public environmentName: string;

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);
  public submitted: boolean = false;
  public pageTitle: string = "Medical History";

  public timeInMilliseconds: string;
  public heightFeetFieldName: string;
  public heightInchesFieldName: string;
  public weightFieldName: string;
  public applicantType: string = this.userSessionService.data.applicantType;
  public formNumber: string = this.userSessionService.data.formNumber;
  public isGenericDynamicForm: boolean = this.userSessionService.data.isGenericDynamicForm;
  public mhsCoverages: CoverageMhs[] = this.userSessionService.data.mhsCoverages;
  public formSubmitted: boolean = false;
  public isPageLoading: boolean = true; // Start as true to prevent back clicks during initial load
  public isEditFromReview: boolean = false;
  public isSavingEdit: boolean = false;
  /** Set to true when the user clicks Back without saving so session cache
   *  is not overwritten with unsaved (possibly empty) form data. */
  private discardFormDataOnDestroy: boolean = false;
  /** Deep-copy of medHistoryFormData taken when the edit session begins, so
   *  clicking Back can restore the original data instead of leaving it empty. */
  private editSessionSnapshot: any = null;
  
  requiredElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'required-error');
  yesNoContentError: string = this.requiredElement?.error;

  medHistoryForm: UntypedFormGroup;
  private saveTimeout: any;

  public yesNoRadioButtonOptions = [
    { name: 'no', label: 'No' },
    { name: 'yes', label: 'Yes' }
  ];

  heightWeightValid: boolean = false;
  maxLengthFeet: number = GlobalConstants.MAX_LENGTH_HW_FEET;
  maxLengthInches: number = GlobalConstants.MAX_LENGTH_HW_INCHES;
  maxLengthWeight: number = GlobalConstants.MAX_LENGTH_HW_WEIGHT;

  feetElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'feet-length-error');
  feetNumElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'feet-number-error');
  inchesElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'inches-length-error');
  inchesNumElement = this.cmService.getContentById(ContentType.ELEMENT, 'inches-number-error');
  weightElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'weight-length-error');
  weightNumElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, 'weight-number-error');
  
  public mhsQuestions: Questions;
  public controls: MhsControl[];

  public questionsWithPhysicianInfo: string[];

  public maxLength: number = GlobalConstants.MAX_LENGTH_GENERIC_INPUT;

  public yesNoControls: string[];

  public cancelEmployeeInfo = {
    action: "button_click",
    category: "employee-info",
    name: "cancel",
  }

  public noHeightAndWeight: boolean = false;
  private renamedControls: RenamedControl[] = [];
  private allIndexes: string[] = [];
  private indexesToReplace: IndexCompare[] = [];
  private subscriptions: Subscription[] = [];
  private restorationComplete: boolean = false;
  private recentlyRestoredCheckboxGroups: Set<string> = new Set(); // Track which checkbox groups were just restored

  constructor(private userSessionService: UserSessionService,
    private fb: FormBuilder,
    private cmService: ManagedContentService, 
    private mhsService: MhsService,
    private authGuardService: AuthGuardService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appInsightsApiService: AppInsightsApiService,
    private modalService: ModalService,
    private windowService: WindowRef,
    private appInsightsLoggerService: AppInsightsLoggerService,
    public formControlUtilitiesService: FormControlUtilitiesService,
    private cdr: ChangeDetectorRef,
    private applicationService: ApplicationService) { }

  ngOnInit(): void {
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }

    // Detect edit-mode entry from the review Overview "View" button.
    // Mirrors the same detection used by applicant-info.component.ts so the page
    // can show Back / Save buttons and persist changes via a partial update
    // instead of the standard Continue / Cancel flow.
    const isEditFromState = history.state && history.state.editMedicalHistory === true;
    const isEditFromQuery = this.activatedRoute.snapshot.queryParamMap.get('mode') === 'edit';
    this.isEditFromReview = isEditFromState || isEditFromQuery;
    
    // Clear the query params from the URL after detection to keep URL clean
    if (isEditFromQuery) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
        replaceUrl: true
      });
    }

    // Reset form submission state when navigating back to this page
    this.formSubmitted = false;
    this.submitted = false;
    
    // CRITICAL: Reset physician modal flag when user enters medical history page
    // This ensures if user goes back and changes answers, the modal logic re-evaluates correctly
    this.userSessionService.data['physicianModalAcknowledged'] = false;
    
    // CRITICAL: Start with page loading to prevent back button clicks during restoration
    this.isPageLoading = true;
    
    // CRITICAL: Start with restoration incomplete
    // Only set to true after data is actually restored or if no restoration needed
    this.restorationComplete = false;

    this.environmentName = environment.name;

    this.setPageTitle();

    let today = new Date();
    this.timeInMilliseconds = today.getTime().toString();
    
    // Check for saved form data in session
    const savedMedHistoryData = this.userSessionService.data['medHistoryFormData'];
    
    // Snapshot the pre-edit state so Back navigation can restore it
    if (this.isEditFromReview && savedMedHistoryData) {
      this.editSessionSnapshot = JSON.parse(JSON.stringify(savedMedHistoryData));
    }
    
    let needsEarlyRestoration = false; // Track if we restored in the reuse block
    
    // Check if form already exists in parent (user navigated back to this page)
    if (this.parentForm && this.parentForm.contains('medHistory')) {
      // Use existing form to preserve data
      this.medHistoryForm = this.parentForm.get('medHistory') as UntypedFormGroup;
      
      // CRITICAL: When reusing form, ALWAYS restore from session storage
      // The form might have stale values from a previous visit (e.g., user changed yes→no→yes→no)
      // Session storage has the correct latest values, form might not
      if (savedMedHistoryData) {
        needsEarlyRestoration = true;
        // Trigger restoration immediately since form might have stale values
        this.restoreFormData(savedMedHistoryData);
      }
    } else {
      // Create new form for first visit
      this.medHistoryForm = new UntypedFormGroup({ });

      if(!this.noHeightAndWeight){
        this.medHistoryForm.addControl("mq-10001-height-feet", this.fb.control('', [Validators.required, Validators.min(1), Validators.max(9), Validators.maxLength(1), Validators.pattern(GlobalConstants.NUMBER_PATTERN)]));
        this.medHistoryForm.addControl("mq-10001-height-inches", this.fb.control('', [Validators.required, Validators.min(0), Validators.max(11), Validators.maxLength(2), Validators.pattern(GlobalConstants.NUMBER_PATTERN)]));
        this.medHistoryForm.addControl("mq-10001-weight", this.fb.control('', [Validators.required, Validators.min(1), Validators.max(999), Validators.maxLength(3), Validators.pattern(GlobalConstants.NUMBER_PATTERN)]));
      }
      
      this.mhsQuestions = this.userSessionService.data.newMhsQuestions;

      if(this.isGenericDynamicForm){
        this.controls = this.userSessionService.data.newMhsQuestions.levelOneQuestions.filter(q => q.use === 'ui');
      }else{
        this.controls = this.userSessionService.data.newMhsQuestions.levelOneQuestions;
      }

      for (let x in this.controls) {
        if(this.isGenericDynamicForm){
          if(this.controls[x].use === 'ui' && this.checkApplicantType(this.applicantType, this.controls[x].applicantType)){
            if (this.controls[x].controlType === 'yesno') {
              this.medHistoryForm.addControl(this.controls[x].key, this.fb.control('', [Validators.required]));
            }
    
            for (let y in this.controls[x].additionalQuestions) {
              if(this.checkApplicantType(this.applicantType, this.controls[x].additionalQuestions[y].applicantType)){
                if (this.controls[x].additionalQuestions[y].controlType === 'yesno') {
                  this.medHistoryForm.addControl(this.controls[x].additionalQuestions[y].key, this.fb.control('', [Validators.required]));
                }
              }
            }
          }
        }else {
          if (this.controls[x].controlType === 'yesno') {
            this.medHistoryForm.addControl(this.controls[x].key, this.fb.control('', [Validators.required]));
          }
          for (let y in this.controls[x].additionalQuestions) {
            if (this.controls[x].additionalQuestions[y].controlType === 'yesno') {
              this.medHistoryForm.addControl(this.controls[x].additionalQuestions[y].key, this.fb.control('', [Validators.required]));
            }
          }
        }
      }
    }

    this.mhsQuestions = this.userSessionService.data.newMhsQuestions;
    if(this.isGenericDynamicForm){
      this.controls = this.userSessionService.data.newMhsQuestions.levelOneQuestions.filter(q => q.use === 'ui');
    }else{
      this.controls = this.userSessionService.data.newMhsQuestions.levelOneQuestions;
    }

    this.loadErrorMessages();

    // Add form to parent if it was newly created
    const wasNewlyCreated = this.parentForm && !this.parentForm.contains('medHistory');
    if (wasNewlyCreated) {
      this.parentForm.addControl('medHistory', this.medHistoryForm);
    }

    // ALWAYS restore saved data when it exists (whether form is new or reused)
    // This ensures checkbox groups and additional detail fields are recreated after navigation
    // Skip if we already restored during the form reuse check above
    if (savedMedHistoryData && !needsEarlyRestoration) {
      this.restoreFormData(savedMedHistoryData);
    } else if (needsEarlyRestoration) {
      // Already restored earlier
    } else {
      // No saved data to restore - form is fresh
      this.restorationComplete = true;
      // Page is fully loaded since no restoration is needed
      this.isPageLoading = false;
    }

    const sub = this.mhsService.submitMedicalHistory.subscribe(msg => {
      if(msg){
        this.onSubmit();
      }
    });
    this.subscriptions.push(sub);

    // Setup form change subscriptions (valueChanges, statusChanges)
    this.setupFormChangeSubscriptions();

    // Monitor parent question changes and remove checkbox groups when answer changes to "no"
    this.setupCheckboxGroupCleanup();
    // Monitor status changes to remove resolution date when status is not "resolved"
    this.setupStatusChangeCleanup();

    this.windowService.nativeDocument
      .querySelector('body')
      .addEventListener('click', (event: Event) => this.onClick(event));
    this.disableAutoFill();
  }//end ngOnInit

  ngAfterViewInit() {
    this.appInsightsApiService.endNavigationEvent(this.router.url);
    
    // Ensure formSubmitted stays false after view init
    this.formSubmitted = false;
    
    // Check if we need to restore data (browser back button case)
    // The restoration might have been triggered in ngOnInit but if the component
    // was recreated (due to routing), we need to check again
    const savedMedHistoryData = this.userSessionService.data['medHistoryFormData'];
    
    // Check if checkbox groups are missing from the form but exist in saved data
    const needsRestoration = savedMedHistoryData && Object.keys(savedMedHistoryData).some(key => {
      if (key.includes('-checkbox-group')) {
        return !this.medHistoryForm.get(key);
      }
      return false;
    });
    
    if (needsRestoration) {
      this.restorationComplete = false; // Reset flag to allow restoration
      this.isPageLoading = true; // Keep loading state during restoration
      this.restoreFormData(savedMedHistoryData);
    } else {
      // No restoration needed - page is fully loaded
      this.isPageLoading = false;
    }
    
    // Setup monitoring in AfterViewInit to ensure it runs every time the view is initialized
    // This catches cases where the component is reused and ngOnInit doesn't run
    if (!this['_monitoringSetupComplete']) {
      this['_monitoringSetupComplete'] = true;
      this.setupCheckboxGroupCleanup();
    }
  }

  ngOnDestroy() {
    
    // DON'T save in ngOnDestroy - the debounced saves have already captured the correct state
    // Saving here can capture corrupted state if web components auto-change values during teardown
    // The debounced saveFormData calls during user interaction are sufficient
    
    // Reset restoration flag so data can be restored again if user navigates back
    this.restorationComplete = false;
    
    // Then unsubscribe from all subscriptions to prevent memory leaks and duplicate calls
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
  }

  private restoreFormData(savedMedHistoryData: any): void {
    
    // CRITICAL: Unsubscribe from valueChanges and statusChanges during restoration
    // to prevent automatic value change events from prism-ui components from being saved
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    
    try {
      // Ensure formSubmitted stays false during restoration
      this.formSubmitted = false;
      this.submitted = false;
      
      // Set restoration flag to prevent cleanup during restoration
      this.restorationComplete = false;
      
      // CRITICAL: Clean up orphaned additional detail fields BEFORE restoration
      // This handles the case where savedData has "mq-10004": "yes" with additional fields,
      // but those fields should have been deleted when user changed to "no"
      const parentRadioKeys = Object.keys(savedMedHistoryData).filter(key => {
        const value = savedMedHistoryData[key];
        return (value === 'yes' || value === 'no') && key.startsWith('mq-');
      });
      
      parentRadioKeys.forEach(parentKey => {
        const savedValue = savedMedHistoryData[parentKey];
        
        // If saved value is "no", check if savedData has ANY related fields
        // If it does, it means they're orphaned - delete them
        if (savedValue === 'no') {
          const checkboxGroupKey = `${parentKey}-checkbox-group`;
          const hasCheckboxGroup = savedMedHistoryData[checkboxGroupKey] !== undefined;
          
          // Check if ANY additional detail fields exist
          const relatedFields: string[] = [];
          Object.keys(savedMedHistoryData).forEach(key => {
            if (key.startsWith(`${parentKey}-`) && key !== checkboxGroupKey && !key.endsWith('-checkbox-group')) {
              relatedFields.push(key);
            }
          });
          
          // If savedData has related fields but parent is "no", clean them up
          if (hasCheckboxGroup || relatedFields.length > 0) {
            
            // Delete checkbox group
            if (hasCheckboxGroup) {
              delete savedMedHistoryData[checkboxGroupKey];
            }
            
            // Delete all additional detail fields
            relatedFields.forEach(field => {
              delete savedMedHistoryData[field];
            });
            
            // CRITICAL: Also update the session storage directly to persist the cleanup
            this.userSessionService.data['medHistoryFormData'] = savedMedHistoryData;
          }
        }
      });
      
      // Synchronously restore simple values first to prevent validation flash
      Object.keys(savedMedHistoryData).forEach(key => {
        const savedValue = savedMedHistoryData[key];
        
        // Only handle simple values synchronously (not nested objects)
        if (typeof savedValue !== 'object') {
          const control = this.medHistoryForm.get(key);
          if (control) {
            control.setValue(savedValue, { emitEvent: false });
            control.markAsUntouched();
            control.markAsPristine();
          }
        }
      });
      
      setTimeout(() => {
        try {
          // Clear the tracking set at start of restoration
          this.recentlyRestoredCheckboxGroups.clear();
          
          // FIRST: Restore nested checkbox groups to parent form BEFORE rendering child components
          Object.keys(savedMedHistoryData).forEach(key => {
        const savedValue = savedMedHistoryData[key];
        
        if (typeof savedValue === 'object' && savedValue !== null) {
          // Track that we're restoring this checkbox group
          this.recentlyRestoredCheckboxGroups.add(key);
          
          // Check if checkbox group already exists (when reusing form)
          const existingGroup = this.medHistoryForm.get(key);
          if (existingGroup && existingGroup instanceof UntypedFormGroup) {
            // Update existing group values WITHOUT emitting events to prevent cleanup
            Object.keys(savedValue).forEach(checkboxKey => {
              const checkboxControl = existingGroup.get(checkboxKey);
              if (checkboxControl) {
                checkboxControl.setValue(savedValue[checkboxKey], { emitEvent: false });
                checkboxControl.markAsUntouched();
                checkboxControl.markAsPristine();
              } else {
                // Add missing checkbox control
                const newControl = this.fb.control(savedValue[checkboxKey]);
                existingGroup.addControl(checkboxKey, newControl);
                newControl.markAsUntouched();
                newControl.markAsPristine();
              }
            });
          } else {
            // Create new FormGroup with the saved checkbox values
            const checkboxGroup = this.fb.group(savedValue);
            // Mark all checkbox controls as untouched
            Object.keys(checkboxGroup.controls).forEach(checkboxKey => {
              const checkbox = checkboxGroup.get(checkboxKey);
              if (checkbox) {
                checkbox.markAsUntouched();
                checkbox.markAsPristine();
              }
            });
            this.medHistoryForm.addControl(key, checkboxGroup);
          }
        }
      });
      
      // SECOND: Restore all simple values (including additional detail fields)
      Object.keys(savedMedHistoryData).forEach(key => {
        const savedValue = savedMedHistoryData[key];
        
        // Skip nested objects (already done above)
        if (typeof savedValue === 'object') {
          return;
        }
        
        const control = this.medHistoryForm.get(key);
        
        if (control) {
          // Control exists - update its value WITHOUT emitting to prevent cleanup
          control.setValue(savedValue, { emitEvent: false });
          // Mark as untouched and pristine to prevent validation errors from showing
          control.markAsUntouched();
          control.markAsPristine();
          control.updateValueAndValidity({ emitEvent: false });
        } else {
          // Control doesn't exist yet (e.g., additional detail fields)
          // Add it to the form so child components can find it
          const newControl = this.fb.control(savedValue);
          this.medHistoryForm.addControl(key, newControl);
          newControl.markAsUntouched();
          newControl.markAsPristine();
        }
      });
      
      // THIRD: Mark all controls as untouched to prevent validation errors during restoration
      Object.keys(this.medHistoryForm.controls).forEach(key => {
        const control = this.medHistoryForm.get(key);
        if (control) {
          control.markAsUntouched();
          control.markAsPristine();
        }
      });
      
      // FOURTH: Change detection to render conditional components (*ngIf based on form values)
      // Child components (mhs-checkbox, additional-details) will now find pre-restored values
      try {
        this.cdr.detectChanges();
      } catch (error) {
        this.appInsightsLoggerService.log('error', 'Med History Component: Change detection error during restoration', error);
      }
      
      // FIFTH: Wait longer for child components to fully initialize before forcing updates
      setTimeout(() => {
        // Mark all checkbox groups and their children as untouched
        Object.keys(this.medHistoryForm.controls).forEach(key => {
          if (key.includes('-checkbox-group')) {
            const checkboxGroup = this.medHistoryForm.get(key);
            if (checkboxGroup instanceof UntypedFormGroup) {
              Object.keys(checkboxGroup.controls).forEach(checkboxKey => {
                const checkbox = checkboxGroup.get(checkboxKey);
                if (checkbox) {
                  checkbox.markAsUntouched();
                  checkbox.markAsPristine();
                }
              });
            }
          }
        });
        
        this.forceWebComponentUpdate();
        
        // Wait a bit more before final change detection
        setTimeout(() => {
          try {
            this.cdr.detectChanges();
          } catch (error) {
            this.appInsightsLoggerService.log('error', 'Med History Component: Second change detection error during restoration', error);
          }
          
          // Page is fully loaded after restoration is complete
          this.isPageLoading = false;
          
          // CRITICAL: Use two-phase completion to prevent prism-ui automatic events from being saved
          // Phase 1: Mark restoration complete after a delay (allows UI to settle)
          // Phase 2: Re-subscribe even later (after any automatic events have fired)
          setTimeout(() => {
            // Mark restoration as complete to allow real user interactions
            this.restorationComplete = true;
            
            // Phase 2: Wait a bit longer before re-subscribing
            // This ensures any automatic prism-ui events fire BEFORE we start listening
            setTimeout(() => {
              this.setupFormChangeSubscriptions();
            }, 300); // 300ms additional delay before re-subscribing
          }, 600); // 600ms for prism-ui to render and stabilize
          
          // Clear the recently restored tracking after 1 second to allow cleanup later
          setTimeout(() => {
            this.recentlyRestoredCheckboxGroups.clear();
          }, 1000);
        }, 150);
      }, 300);
        } catch (innerError) {
          this.appInsightsLoggerService.log('error', 'Med History Component: Error during restoration setTimeout', innerError);
          // Mark restoration as complete even on error to prevent form from being stuck
          this.restorationComplete = true;
          this.isPageLoading = false;
        }
      }, 150);
    } catch (outerError) {
      this.appInsightsLoggerService.log('error', 'Med History Component: Error in restoreFormData', outerError);
      // Mark restoration as complete even on error to prevent form from being stuck
      this.restorationComplete = true;
      this.isPageLoading = false;
    }
  }

  private saveFormData(forceSave: boolean = false): void {
    // CRITICAL: When forceSave is true from ngOnDestroy, ALWAYS allow save
    // But if forceSave is false and restoration is in progress, skip
    if (!forceSave && !this.restorationComplete) {
      return;
    }
    
    if (this.medHistoryForm) {
      const formValue = this.medHistoryForm.getRawValue();
      
      // CRITICAL FIX: Only remove empty checkbox groups if restoration is complete
      // During restoration, checkbox groups may temporarily have all false values
      // while child components are initializing - we must preserve them
      // ALSO: Don't remove checkbox groups that were just restored - they need time to populate
      const cleanedFormValue = { ...formValue };
      if (this.restorationComplete) {
        Object.keys(cleanedFormValue).forEach(key => {
          if (key.includes('-checkbox-group')) {
            // CRITICAL: Don't save checkbox groups that were recently restored - they may not be fully populated yet
            // Remove them from save data to preserve the good data already in session storage
            if (this.recentlyRestoredCheckboxGroups.has(key)) {
              delete cleanedFormValue[key];
              return;
            }
            
            // Extract the parent question ID (e.g., "mq-10033" from "mq-10033-checkbox-group")
            const parentQuestionId = key.replace('-checkbox-group', '');
            const parentQuestionValue = cleanedFormValue[parentQuestionId];
            
            const groupValue = cleanedFormValue[key];
            const hasAnyChecked = groupValue && typeof groupValue === 'object' 
              ? Object.values(groupValue).some((v: any) => v === true)
              : false;
            
            // Remove checkbox group if:
            // 1. Parent radio is "no" (user changed from yes to no), OR
            // 2. Parent radio is "yes" BUT all checkboxes are false (child components haven't populated yet)
            if (parentQuestionValue === 'no' || parentQuestionValue === '' || parentQuestionValue === null || parentQuestionValue === undefined) {
              delete cleanedFormValue[key];
            } else if (!hasAnyChecked) {
              delete cleanedFormValue[key];
            }
          }
        });
      } else {
        // CRITICAL: During restoration, remove ALL checkbox groups - they have unreliable values
        // The good data is already in session storage from before navigation
        Object.keys(cleanedFormValue).forEach(key => {
          if (key.includes('-checkbox-group')) {
            delete cleanedFormValue[key];
          }
        });
      }
      
      const hasData = Object.values(formValue).some(value => 
        value !== null && value !== undefined && value !== ''
      );
      
      if (hasData) {
        // Check if current data has fewer keys than saved data
        // This prevents overwriting good data with incomplete data during component recreation
        const currentSavedData = this.userSessionService.data['medHistoryFormData'];
        const currentKeyCount = Object.keys(formValue).length;
        const savedKeyCount = currentSavedData ? Object.keys(currentSavedData).length : 0;
        
        // Detect if saved data had checkbox groups (indicates user previously selected "yes")
        const savedHadCheckboxGroups = currentSavedData && 
          Object.keys(currentSavedData).some(key => key.includes('-checkbox-group'));
        
        // Detect if saved data had additional detail fields (also indicates user previously selected "yes")
        const savedHadNestedFields = currentSavedData && 
          Object.keys(currentSavedData).some(key => 
            key.includes('-onsetDate') || 
            key.includes('-treatment') || 
            key.includes('-selectCurrentStatus') ||
            key.includes('-month') ||
            key.includes('-year') ||
            key.includes('-diagnosis') ||
            key.includes('-diagnosisDate') ||
            key.includes('-currentStatus') ||
            key.includes('-chronicAcute') ||
            key.includes('-condition') ||
            key.includes('-frequency') ||
            key.includes('-duration') ||
            key.includes('-cause') ||
            key.includes('-type') ||
            key.includes('-description') ||
            key.includes('-limitations') ||
            key.includes('-descriptionOfReasons')
          );
        
        // Detect if current data has checkbox groups
        const currentHasCheckboxGroups = Object.keys(cleanedFormValue).some(key => key.includes('-checkbox-group'));
        
        // Detect if current data has any nested fields (date pickers, treatments, status, chronic/acute radios, etc.)
        const currentHasNestedFields = Object.keys(cleanedFormValue).some(key => 
          key.includes('-onsetDate') || 
          key.includes('-treatment') || 
          key.includes('-selectCurrentStatus') ||
          key.includes('-month') ||
          key.includes('-year') ||
          key.includes('-diagnosis') ||
          key.includes('-diagnosisDate') ||
          key.includes('-currentStatus') ||
          key.includes('-chronicAcute') ||
          key.includes('-condition') || // Chronic/Acute radio buttons
          key.includes('-frequency') ||
          key.includes('-duration') ||
          key.includes('-cause') ||
          key.includes('-type') ||
          key.includes('-description') ||
          key.includes('-limitations') ||
          key.includes('-descriptionOfReasons')
        );
        
        // Check if this is a legitimate user change from "yes" to "no"
        // MUST meet ALL criteria:
        // 1. Saved data HAD checkbox groups or nested fields (user previously answered "yes")
        // 2. Current data has NO checkbox groups AND NO nested fields (user changed to "no")
        // 3. Component restoration is complete (not mid-initialization)
        const isUserChangeFromYesToNo = (savedHadCheckboxGroups || savedHadNestedFields) && 
          !currentHasCheckboxGroups && 
          !currentHasNestedFields &&
          this.restorationComplete; // Only allow if restoration finished (not mid-init)
        
        // Save if:
        // 1. We have more or equal keys than before (normal case)
        // 2. OR forceSave is true from ngOnDestroy (ALWAYS save when component is being destroyed)
        // 3. OR forceSave is true AND current data is meaningful (has more than just base controls)
        //    Base controls = 25 (height, weight, all yes/no questions)
        //    If we have checkbox groups or additional details, we have more than 25
        // 4. OR forceSave is true AND this is a user change from yes to no
        const hasCheckboxOrAdditionalData = currentKeyCount > 25;
        
        // CRITICAL FIX: Only save during destruction if restoration is complete OR we have same/more keys
        // Don't overwrite complete data (26 keys with checkbox groups) with incomplete data (25 keys without)
        const isDestroyingSave = forceSave && currentKeyCount >= 25 && 
          (this.restorationComplete || currentKeyCount >= savedKeyCount);
        const shouldForceSave = forceSave && (hasCheckboxOrAdditionalData || isUserChangeFromYesToNo);
        
        if (currentKeyCount >= savedKeyCount || isDestroyingSave || shouldForceSave) {
          if (shouldForceSave && currentKeyCount < savedKeyCount) {
            // User changed from yes to no (intentional reduction in data)
          }
          
          // CRITICAL: Merge with existing data instead of replacing
          // This preserves checkbox groups that were removed from cleanedFormValue but exist in session storage
          const existingData = this.userSessionService.data['medHistoryFormData'] || {};
          const mergedData = { ...existingData };
          
          // FIRST: Check if cleanedFormValue has parent radios set to "no" but existingData has related fields
          // This catches the case where user changed from "yes" to "no" and cleanup removed fields from form,
          // but existingData still has those fields from when parent was "yes"
          
          const parentRadioKeys = Object.keys(cleanedFormValue).filter(key => {
            const value = cleanedFormValue[key];
            return (value === 'yes' || value === 'no') && key.startsWith('mq-');
          });
          
          
          parentRadioKeys.forEach(parentKey => {
            const currentValue = cleanedFormValue[parentKey];
            const existingValue = existingData[parentKey];
            
            // If current value is "no", check if existingData has ANY related fields
            // If it does, it means user changed from "yes" to "no" - delete them all
            if (currentValue === 'no') {
              const checkboxGroupKey = `${parentKey}-checkbox-group`;
              const hasCheckboxGroup = existingData[checkboxGroupKey] !== undefined;
              
              // Check if ANY additional detail fields exist
              const hasAdditionalDetails = Object.keys(existingData).some(key => 
                key.startsWith(`${parentKey}-`) && 
                key !== checkboxGroupKey &&
                !key.endsWith('-checkbox-group')
              );
              
              // If existingData has related fields but current value is "no", clean up
              if (hasCheckboxGroup || hasAdditionalDetails) {
                
                // Delete checkbox group
                if (hasCheckboxGroup) {
                  delete mergedData[checkboxGroupKey];
                }
                
                // Delete all additional detail fields
                Object.keys(mergedData).forEach(key => {
                  if (key.startsWith(`${parentKey}-`) && key !== checkboxGroupKey) {
                    delete mergedData[key];
                  }
                });
              }
            }
          });
          
          // SECOND: Update with new values from cleanedFormValue
          Object.keys(cleanedFormValue).forEach(key => {
            mergedData[key] = cleanedFormValue[key];
          });
          
          // THIRD: Preserve checkbox groups from session storage if they're not in cleanedFormValue
          // but their parent radio is "yes"
          Object.keys(existingData).forEach(key => {
            if (key.includes('-checkbox-group') && !cleanedFormValue[key]) {
              const parentQuestionId = key.replace('-checkbox-group', '');
              const parentValue = mergedData[parentQuestionId]; // Use mergedData which has updated values
              
              if (parentValue === 'yes') {
                mergedData[key] = existingData[key];
              } else {
                delete mergedData[key];
              }
            }
          });
          
          // FOURTH: Final cleanup - remove any orphaned additional detail fields
          // Additional detail fields have patterns like: mq-10004-descriptionOfReasons, mq-10004-limitations, etc.
          // BUT NOT fields like mq-10001-height-feet which are regular inputs (not conditional on a yes/no radio)
          Object.keys(mergedData).forEach(key => {
            // Skip checkbox groups - they're handled separately above
            if (key.includes('-checkbox-group')) {
              return;
            }
            
            // Check if this is an additional detail field (has a dash followed by a field name)
            const dashCount = (key.match(/-/g) || []).length;
            if (dashCount >= 2) { // e.g., "mq-10004-descriptionOfReasons" has 2 dashes
              // Extract parent question ID (e.g., "mq-10004" from "mq-10004-descriptionOfReasons")
              const parts = key.split('-');
              if (parts.length >= 3) {
                const parentQuestionId = parts.slice(0, 2).join('-'); // e.g., "mq-10004"
                const parentValue = mergedData[parentQuestionId];
                
                
                // ONLY delete if:
                // 1. Parent EXISTS in mergedData
                // 2. Parent value is a yes/no answer ("yes" or "no") - confirms it's a conditional field
                // 3. Parent value is "no" - the condition that triggers deletion
                // This prevents deleting fields like mq-10001-height-feet where:
                // - There's no parent radio (mq-10001 doesn't exist)
                // - They're just regular form fields with dashes in the name
                const parentExists = parentValue !== undefined;
                const parentIsYesNo = parentValue === 'yes' || parentValue === 'no';
                const parentIsNo = parentValue === 'no';
                
                if (parentExists && parentIsYesNo && parentIsNo) {
                  delete mergedData[key];
                } else if (!parentExists) {
                } else if (!parentIsYesNo) {
                }
              }
            }
          });
          
          this.userSessionService.data['medHistoryFormData'] = mergedData;
          
          // CRITICAL: Verify the save actually persisted by reading it back
          const verifyData = this.userSessionService.data['medHistoryFormData'];
          const verifyMq10004 = verifyData ? verifyData['mq-10004'] : 'NOT_FOUND';
          
          // Log checkbox groups specifically (optional - remove if not needed)
          const checkboxKeys = Object.keys(mergedData).filter(k => k.includes('-checkbox-group'));
          if (checkboxKeys.length > 0) {
            checkboxKeys.forEach(key => {
              const groupValue = mergedData[key];
              const hasAnyChecked = groupValue && typeof groupValue === 'object' && 
                Object.values(groupValue).some((v: any) => v === true);
            });
          }
        } else {
        }
      } else {
      }
    }
  }

  private setupFormChangeSubscriptions() {
    // Auto-save form data whenever it changes (to capture nested checkbox groups)
    const formSub = this.medHistoryForm.valueChanges.subscribe(() => {
      // Debounce: only save after 500ms of no changes
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.saveFormData();
      }, 500);
    });
    this.subscriptions.push(formSub);

    // Also watch for status changes (when controls are added/removed by child components)
    const statusSub = this.medHistoryForm.statusChanges.subscribe(() => {
      // Save after a short delay when form structure changes (new controls added)
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.saveFormData();
      }, 800); // Slightly longer delay to let child components finish initialization
    });
    this.subscriptions.push(statusSub);
  }

  private setupCheckboxGroupCleanup() {
    // Monitor all questions that have child controls (checkboxes or additional details)
    // and remove them when the parent answer changes to "no"
    if (this.controls) {
      this.controls.forEach(control => {
        const questionKey = control.key;
        const questionControl = this.medHistoryForm.get(questionKey);
        
        if (questionControl) {
          const sub = questionControl.valueChanges.subscribe(value => {
            // Skip cleanup if we're in the middle of restoration
            if (!this.restorationComplete) {
              return;
            }
            
            if (value === 'no' || value === '' || value === null) {
              // Remove checkbox group if it exists
              if (control.levelTwoQuestions && control.levelTwoQuestions.length > 0) {
                const checkboxGroupKey = questionKey + '-checkbox-group';
                if (this.medHistoryForm.get(checkboxGroupKey)) {
                  this.medHistoryForm.removeControl(checkboxGroupKey);
                  
                  const savedData = this.userSessionService.data['medHistoryFormData'];
                  if (savedData && savedData[checkboxGroupKey]) {
                    delete savedData[checkboxGroupKey];
                  }
                }
                
                // For each checkbox in the group, remove ALL related fields
                control.levelTwoQuestions.forEach(checkbox => {
                  const checkboxKey = checkbox.key;
                  // Find all form controls that start with this checkbox key
                  const controlsToRemove = Object.keys(this.medHistoryForm.controls).filter(key => 
                    key.startsWith(checkboxKey + '-')
                  );
                  
                  // If checkbox has additionalDetailsHasPhysicianInfo, also remove standard physician info fields
                  if (checkbox.additionalDetailsHasPhysicianInfo) {
                    const standardPhysicianFields = [
                      `${checkboxKey}-treatment`,
                      `${checkboxKey}-onsetDate`,
                      `${checkboxKey}-onsetDate-month`,
                      `${checkboxKey}-onsetDate-year`,
                      `${checkboxKey}-resolutionDate`,
                      `${checkboxKey}-resolutionDate-month`,
                      `${checkboxKey}-resolutionDate-year`,
                      `${checkboxKey}-selectCurrentStatus`,
                      `${checkboxKey}-physicianName`,
                      `${checkboxKey}-physicianSpecialty`,
                      `${checkboxKey}-physicianLastVisitMonthName`,
                      `${checkboxKey}-physicianLastVisitYearName`
                    ];
                    
                    // Add these to the removal list if they're not already there
                    standardPhysicianFields.forEach(field => {
                      if (!controlsToRemove.includes(field)) {
                        controlsToRemove.push(field);
                      }
                    });
                  }
                  
                  controlsToRemove.forEach(controlKey => {
                    const ctrl = this.medHistoryForm.get(controlKey);
                    if (ctrl) {
                      // Clear validators and errors before removing to prevent validation errors
                      ctrl.clearValidators();
                      ctrl.setErrors(null);
                      ctrl.updateValueAndValidity();
                      this.medHistoryForm.removeControl(controlKey);
                      
                      const savedData = this.userSessionService.data['medHistoryFormData'];
                      if (savedData && savedData[controlKey]) {
                        delete savedData[controlKey];
                      }
                    }
                  });
                });
              }
              
              // Also remove any additional detail fields defined in the control
              if (control.additionalDetails && control.additionalDetails.length > 0) {
                control.additionalDetails.forEach(detail => {
                  const detailKey = detail.key;
                  
                  // For month-picker controls, we need to remove the main control AND the month/year fields
                  const controlsToRemoveForDetail = [detailKey];
                  if (detail.controlType === 'monthPicker') {
                    controlsToRemoveForDetail.push(`${detailKey}-month`);
                    controlsToRemoveForDetail.push(`${detailKey}-year`);
                  }
                  
                  controlsToRemoveForDetail.forEach(key => {
                    const ctrl = this.medHistoryForm.get(key);
                    if (ctrl) {
                      // Clear validators and errors before removing to prevent validation errors
                      ctrl.clearValidators();
                      ctrl.setErrors(null);
                      ctrl.updateValueAndValidity();
                      this.medHistoryForm.removeControl(key);
                      
                      const savedData = this.userSessionService.data['medHistoryFormData'];
                      if (savedData && savedData[key]) {
                        delete savedData[key];
                      }
                    }
                  });
                });
              }
            }
          });
          this.subscriptions.push(sub);
        }
        
        // Also check additionalQuestions (nested questions like pregnancy)
        if (control.additionalQuestions && control.additionalQuestions.length > 0) {
          control.additionalQuestions.forEach(subControl => {
            const subQuestionKey = subControl.key;
            const subQuestionControl = this.medHistoryForm.get(subQuestionKey);
            
            if (subQuestionControl) {
              const sub = subQuestionControl.valueChanges.subscribe(value => {
                if (value === 'no' || value === '' || value === null) {
                  // Remove checkbox group if it exists
                  if (subControl.levelTwoQuestions && subControl.levelTwoQuestions.length > 0) {
                    const subCheckboxGroupKey = subQuestionKey + '-checkbox-group';
                    if (this.medHistoryForm.get(subCheckboxGroupKey)) {
                      this.medHistoryForm.removeControl(subCheckboxGroupKey);
                      
                      const savedData = this.userSessionService.data['medHistoryFormData'];
                      if (savedData && savedData[subCheckboxGroupKey]) {
                        delete savedData[subCheckboxGroupKey];
                      }
                    }
                    
                    // For each checkbox in the nested group, remove ALL related fields
                    subControl.levelTwoQuestions.forEach(checkbox => {
                      const checkboxKey = checkbox.key;
                      // Find all form controls that start with this checkbox key
                      const controlsToRemove = Object.keys(this.medHistoryForm.controls).filter(key => 
                        key.startsWith(checkboxKey + '-')
                      );
                      
                      // If checkbox has additionalDetailsHasPhysicianInfo, also remove standard physician info fields
                      if (checkbox.additionalDetailsHasPhysicianInfo) {
                        const standardPhysicianFields = [
                          `${checkboxKey}-treatment`,
                          `${checkboxKey}-onsetDate`,
                          `${checkboxKey}-onsetDate-month`,
                          `${checkboxKey}-onsetDate-year`,
                          `${checkboxKey}-resolutionDate`,
                          `${checkboxKey}-resolutionDate-month`,
                          `${checkboxKey}-resolutionDate-year`,
                          `${checkboxKey}-selectCurrentStatus`,
                          `${checkboxKey}-physicianName`,
                          `${checkboxKey}-physicianSpecialty`,
                          `${checkboxKey}-physicianLastVisitMonthName`,
                          `${checkboxKey}-physicianLastVisitYearName`
                        ];
                        
                        standardPhysicianFields.forEach(field => {
                          if (!controlsToRemove.includes(field)) {
                            controlsToRemove.push(field);
                          }
                        });
                      }
                      
                      controlsToRemove.forEach(controlKey => {
                        const ctrl = this.medHistoryForm.get(controlKey);
                        if (ctrl) {
                          // Clear validators and errors before removing to prevent validation errors
                          ctrl.clearValidators();
                          ctrl.setErrors(null);
                          ctrl.updateValueAndValidity();
                          this.medHistoryForm.removeControl(controlKey);
                          
                          const savedData = this.userSessionService.data['medHistoryFormData'];
                          if (savedData && savedData[controlKey]) {
                            delete savedData[controlKey];
                          }
                        }
                      });
                    });
                  }
                  
                  // Remove additional detail fields if they exist
                  if (subControl.additionalDetails && subControl.additionalDetails.length > 0) {
                    subControl.additionalDetails.forEach(detail => {
                      const detailKey = detail.key;
                      if (this.medHistoryForm.get(detailKey)) {
                        this.medHistoryForm.removeControl(detailKey);
                        
                        const savedData = this.userSessionService.data['medHistoryFormData'];
                        if (savedData && savedData[detailKey]) {
                          delete savedData[detailKey];
                        }
                      }
                    });
                  }
                }
              });
              this.subscriptions.push(sub);
            }
          });
        }
      });
    }
    
    // Monitor individual checkbox changes within checkbox groups
    // When a checkbox is unchecked, remove its additional detail fields
    this.monitorCheckboxChanges();
  }

  private monitorCheckboxChanges() {
    // Helper function to set up monitoring for a specific checkbox
    const setupCheckboxMonitoring = (checkbox: any, checkboxControl: any, isNested: boolean = false) => {
      if (checkboxControl['_monitoringSetup']) {
        return; // Already set up
      }
      
      checkboxControl['_monitoringSetup'] = true;
      // Store the initial value to detect actual changes (not just initial state)
      let previousValue = checkboxControl.value;
      
      const sub = checkboxControl.valueChanges.subscribe(value => {
        // Skip cleanup if we're in the middle of restoration
        if (!this.restorationComplete) {
          previousValue = value; // Update for next change
          return;
        }
        
        // Only trigger cleanup if checkbox was previously checked and is now unchecked
        // Don't trigger on initial false value or when checking the box
        if ((value === false || value === 'false') && (previousValue === true || previousValue === 'true')) {
          // IMMEDIATELY clear session storage
          const savedData = this.userSessionService.data['medHistoryFormData'];
          if (savedData) {
            const keysToRemove = Object.keys(savedData).filter(key => key.startsWith(checkbox.key + '-'));
            keysToRemove.forEach(key => {
              delete savedData[key];
            });
          }
          
          setTimeout(() => {
            const controlsToRemove = Object.keys(this.medHistoryForm.controls).filter(key => 
              key.startsWith(checkbox.key + '-')
            );
            
            // Add physician fields if needed
            if (checkbox.additionalDetailsHasPhysicianInfo) {
              const standardPhysicianFields = [
                `${checkbox.key}-treatment`,
                `${checkbox.key}-onsetDate`,
                `${checkbox.key}-onsetDate-month`,
                `${checkbox.key}-onsetDate-year`,
                `${checkbox.key}-resolutionDate`,
                `${checkbox.key}-resolutionDate-month`,
                `${checkbox.key}-resolutionDate-year`,
                `${checkbox.key}-selectCurrentStatus`,
                `${checkbox.key}-physicianName`,
                `${checkbox.key}-physicianSpecialty`,
                `${checkbox.key}-physicianLastVisitMonthName`,
                `${checkbox.key}-physicianLastVisitYearName`
              ];
              
              standardPhysicianFields.forEach(field => {
                if (!controlsToRemove.includes(field)) {
                  controlsToRemove.push(field);
                }
              });
            }
            
            controlsToRemove.forEach(controlKey => {
              const ctrl = this.medHistoryForm.get(controlKey);
              if (ctrl) {
                ctrl.clearValidators();
                ctrl.setErrors(null);
                ctrl.updateValueAndValidity();
                this.medHistoryForm.removeControl(controlKey);
              }
            });
            
            this.medHistoryForm.updateValueAndValidity();
            
            setTimeout(() => {
              this.saveFormData();
            }, 50);
          }, 0);
        }
        
        // Update previous value for next change detection
        previousValue = value;
      });
      this.subscriptions.push(sub);
    };
    
    // Helper function to scan and set up monitoring for all existing checkbox groups
    const setupMonitoring = () => {
      if (!this.controls) return;
      
      this.controls.forEach(control => {
        // Regular checkbox groups
        if (control.levelTwoQuestions && control.levelTwoQuestions.length > 0) {
          const checkboxGroupKey = control.key + '-checkbox-group';
          const checkboxGroup = this.medHistoryForm.get(checkboxGroupKey);
          
          if (checkboxGroup && checkboxGroup instanceof UntypedFormGroup) {
            control.levelTwoQuestions.forEach(checkbox => {
              const checkboxControl = checkboxGroup.get(checkbox.key);
              if (checkboxControl) {
                setupCheckboxMonitoring(checkbox, checkboxControl, false);
              }
            });
          }
        }
        
        // Nested checkbox groups
        if (control.additionalQuestions && control.additionalQuestions.length > 0) {
          control.additionalQuestions.forEach(subControl => {
            if (subControl.levelTwoQuestions && subControl.levelTwoQuestions.length > 0) {
              const subCheckboxGroupKey = subControl.key + '-checkbox-group';
              const subCheckboxGroup = this.medHistoryForm.get(subCheckboxGroupKey);
              
              if (subCheckboxGroup && subCheckboxGroup instanceof UntypedFormGroup) {
                subControl.levelTwoQuestions.forEach(checkbox => {
                  const checkboxControl = subCheckboxGroup.get(checkbox.key);
                  if (checkboxControl) {
                    setupCheckboxMonitoring(checkbox, checkboxControl, true);
                  }
                });
              }
            }
          });
        }
      });
    };
    
    // Run initial setup immediately
    setupMonitoring();
    
    // Set up continuous monitoring to catch checkbox groups created dynamically (when user clicks "yes")
    // Store interval ID so we can clean it up in ngOnDestroy
    const pollInterval = setInterval(() => {
      setupMonitoring();
    }, 200); // Check every 200ms for new checkbox groups
    
    // Store the interval so we can clear it in ngOnDestroy
    this.subscriptions.push({
      unsubscribe: () => {
        clearInterval(pollInterval);
      }
    } as any);
  }

  private setupStatusChangeCleanup() {
    // Monitor status changes (resolved/ongoing/cured) to remove resolution date when needed
    // Status controls are nested FormGroups like: mq-10100-selectCurrentStatus.mq-10100-selectCurrentStatus
    
    // Use a small delay to ensure form is fully initialized
    setTimeout(() => {
      const allControls = Object.keys(this.medHistoryForm.controls);
      
      // Find all status controls (they end with '-selectCurrentStatus')
      const statusControlKeys = allControls.filter(key => key.endsWith('-selectCurrentStatus'));
      
      statusControlKeys.forEach(statusKey => {
        const statusFormGroup = this.medHistoryForm.get(statusKey);
        
        if (statusFormGroup && statusFormGroup instanceof UntypedFormGroup) {
          // Get the inner control (nested with same name)
          const innerStatusControl = statusFormGroup.get(statusKey);
          
          if (innerStatusControl) {
            const sub = innerStatusControl.valueChanges.subscribe(value => {
              // If status is NOT "resolved", remove resolution date fields
              if (value !== 'resolved') {
                // Extract the base key (e.g., 'mq-10100' from 'mq-10100-selectCurrentStatus')
                const baseKey = statusKey.replace('-selectCurrentStatus', '');
                
                // Remove resolution date fields
                const resolutionDateKey = baseKey + '-resolutionDate';
                const resolutionMonthKey = baseKey + '-resolutionDate-month';
                const resolutionYearKey = baseKey + '-resolutionDate-year';
                
                [resolutionDateKey, resolutionMonthKey, resolutionYearKey].forEach(key => {
                  if (this.medHistoryForm.get(key)) {
                    this.medHistoryForm.removeControl(key);
                    
                    const savedData = this.userSessionService.data['medHistoryFormData'];
                    if (savedData && savedData[key]) {
                      delete savedData[key];
                    }
                  }
                });
              }
            });
            this.subscriptions.push(sub);
          }
        }
      });
    }, 500);
  }
  
  loadErrorMessages(){
    this.requiredElement = this.cmService.getContentById(ContentType.ELEMENT, 'required-error');
    this.feetElement = this.cmService.getContentById(ContentType.ELEMENT, 'feet-length-error');
    this.feetNumElement = this.cmService.getContentById(ContentType.ELEMENT, 'feet-number-error');
    this.inchesElement = this.cmService.getContentById(ContentType.ELEMENT, 'inches-length-error');
    this.inchesNumElement = this.cmService.getContentById(ContentType.ELEMENT, 'inches-number-error');
    this.weightElement = this.cmService.getContentById(ContentType.ELEMENT, 'weight-length-error');
    this.weightNumElement = this.cmService.getContentById(ContentType.ELEMENT, 'weight-number-error');
  }

  private forceWebComponentUpdate() {
    try {
      // Handle all prism-ui input component types
      const componentSelectors = [
        'prism-ui-input-text',
        'prism-ui-input-number',
        'prism-ui-input-currency',
        'prism-ui-radio-button'
      ];

      // Helper function to recursively find a control in nested form groups
      const findControlRecursive = (form: any, controlName: string): any => {
        // Check direct children first
        if (form.get(controlName)) {
          return form.get(controlName);
        }
        
        // Check in nested form groups
        const controls = form.controls || {};
        for (const key in controls) {
          const control = controls[key];
          if (control instanceof UntypedFormGroup) {
            const found = findControlRecursive(control, controlName);
            if (found) return found;
          }
        }
        
        return null;
      };

      componentSelectors.forEach(selector => {
        try {
          const inputs = document.querySelectorAll(selector);
          inputs.forEach((input: any) => {
            try {
              const formControlName = input.getAttribute('formControlName');
              if (formControlName) {
                // Try to find the control in the form hierarchy
                const control = findControlRecursive(this.medHistoryForm, formControlName);
                const controlValue = control?.value;
                if (control && controlValue !== null && controlValue !== undefined && controlValue !== '') {
                  // Try to set the value on the web component's shadow DOM
                  if (input.shadowRoot) {
                    const innerInput = input.shadowRoot.querySelector('input');
                    if (innerInput) {
                      innerInput.value = controlValue;
                      // Dispatch input event to trigger any listeners
                      innerInput.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                  }
                  
                  // Set the defaultValue property
                  input.defaultValue = controlValue;
                  
                  // Also try setting the value property directly
                  if (input.value !== controlValue) {
                    input.value = controlValue;
                  }
                  
                  // For radio buttons, set checked state
                  if (selector === 'prism-ui-radio-button') {
                    const inputValue = input.getAttribute('value');
                    if (inputValue === controlValue) {
                      input.setAttribute('checked', 'true');
                    }
                  }
                }
              }
            } catch (inputError) {
              // Error updating individual web component - continue with next
            }
          });
        } catch (selectorError) {
          // Error processing selector - continue with next
        }
      });
    } catch (error) {
      this.appInsightsLoggerService.log('error', 'Med History Component: Error in forceWebComponentUpdate', error);
    }
  }

  private disableAutoFill(){

    // Add autocomplete='off' to form elements
    Array.from(document.querySelectorAll('form'))
    .forEach(element => {
      element.setAttribute('autocomplete', 'off');
    });

    Array.from(document.querySelectorAll('input[matInput]'))
    .forEach(element => {
      // Set name equal to id if name attribute doesn't exist
      if(!element.getAttribute("name")){
        element.setAttribute('name', element.getAttribute("id")? element.getAttribute("id"): '');
      }
       // Prepend element names to make them appear unique
      if(element.getAttribute("name") && element.getAttribute("name").indexOf(this.timeInMilliseconds) === -1){
          element.setAttribute('name', this.timeInMilliseconds + "-" + element.getAttribute("name"));
      }

      // Set value of autocomplete attribute equal to element name
      // Modern browsers (particularly Chrome) don't honor autocomplete='off'
      element.setAttribute('autocomplete', element.getAttribute("name"));
    });

    this.heightFeetFieldName = this.timeInMilliseconds + "-mq-10001-height-feet";
    this.heightInchesFieldName = this.timeInMilliseconds + "-mq-10001-height-inches";
    this.weightFieldName = this.timeInMilliseconds + "-mq-10001-weight";
  }

  private cleanupUncheckedCheckboxFields() {
    // Before form submission, remove all additional detail fields for unchecked checkboxes
    if (this.controls) {
      this.controls.forEach(control => {
        if (control.levelTwoQuestions && control.levelTwoQuestions.length > 0) {
          const checkboxGroupKey = control.key + '-checkbox-group';
          const checkboxGroup = this.medHistoryForm.get(checkboxGroupKey);
          
          if (checkboxGroup && checkboxGroup instanceof UntypedFormGroup) {
            control.levelTwoQuestions.forEach(checkbox => {
              const checkboxControl = checkboxGroup.get(checkbox.key);
              const isChecked = checkboxControl?.value === true || checkboxControl?.value === 'true';
              
              if (!isChecked) {
                // Find all controls that start with this checkbox key
                const controlsToRemove = Object.keys(this.medHistoryForm.controls).filter(key => 
                  key.startsWith(checkbox.key + '-')
                );
                
                controlsToRemove.forEach(controlKey => {
                  const ctrl = this.medHistoryForm.get(controlKey);
                  if (ctrl) {
                    ctrl.clearValidators();
                    ctrl.setErrors(null);
                    this.medHistoryForm.removeControl(controlKey);
                  }
                });
              }
            });
          }
        }
        
        // Also check nested additional questions
        if (control.additionalQuestions && control.additionalQuestions.length > 0) {
          control.additionalQuestions.forEach(subControl => {
            if (subControl.levelTwoQuestions && subControl.levelTwoQuestions.length > 0) {
              const subCheckboxGroupKey = subControl.key + '-checkbox-group';
              const subCheckboxGroup = this.medHistoryForm.get(subCheckboxGroupKey);
              
              if (subCheckboxGroup && subCheckboxGroup instanceof UntypedFormGroup) {
                subControl.levelTwoQuestions.forEach(checkbox => {
                  const checkboxControl = subCheckboxGroup.get(checkbox.key);
                  const isChecked = checkboxControl?.value === true || checkboxControl?.value === 'true';
                  
                  if (!isChecked) {
                    const controlsToRemove = Object.keys(this.medHistoryForm.controls).filter(key => 
                      key.startsWith(checkbox.key + '-')
                    );
                    
                    controlsToRemove.forEach(controlKey => {
                      const ctrl = this.medHistoryForm.get(controlKey);
                      if (ctrl) {
                        ctrl.clearValidators();
                        ctrl.setErrors(null);
                        this.medHistoryForm.removeControl(controlKey);
                      }
                    });
                  }
                });
              }
            }
          });
        }
      });
    }
    
    // Force form to revalidate after cleanup
    this.medHistoryForm.updateValueAndValidity();
  }

  onSubmit(){
    // Before validating, ensure any unchecked checkbox fields are removed
    this.cleanupUncheckedCheckboxFields();
    
    // CRITICAL: Force save immediately after cleanup to capture the cleaned state
    // This must happen even during restoration window because user is submitting
    this.saveFormData(true);
    
    this.formSubmitted = true;
    const spinnerStartTime = new Date().getTime();
    this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Started.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    this.medHistoryForm.markAllAsTouched();
    
    // Trigger change detection to ensure error messages appear immediately
    this.cdr.detectChanges();

    if(!this.medHistoryForm.valid){
      // Set formSubmitted to false FIRST to hide spinner immediately
      this.submitted = false;
      this.formSubmitted = false;
      
      this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Stopped.  There was an invalid control.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      
      // Trigger change detection to hide the spinner and render error messages
      // Then scroll to the first invalid control after rendering completes
      setTimeout(() => {
        this.cdr.detectChanges();
        
        // Now scroll to the first invalid control after errors are rendered
        try {
          this.scrollToFirstInvalidControl();
        } catch (scrollError) {
          this.appInsightsLoggerService.log('error', 'Medical History Component: Error during scroll to invalid control', scrollError);
        }
      }, 0);
    } else {
      if(this.environmentName === 'demonstration'){
        this.gatherAnswers();
        this.userSessionService.data.mhsApplication.companyId = this.userSessionService.data.companyId;
        this.userSessionService.data.mhsApplication.formNumber = this.userSessionService.data.formNumber;

        this.setPhysicianInfoFlag();

        if(this.isGenericDynamicForm){
          this.setAnswers();
        }
        this.submitted = true;
        this.val.emit(this.submitted);
      } else {
        this.gatherAnswers();
        this.userSessionService.data.mhsApplication.companyId = this.userSessionService.data.companyId;
        this.userSessionService.data.mhsApplication.formNumber = this.userSessionService.data.formNumber;

        this.setPhysicianInfoFlag();

        if(this.isGenericDynamicForm){
          this.setAnswers();
        }
        this.updateMedicalHistory(spinnerStartTime);
      }
    }
  }

  scrollToFirstInvalidControl(){
    var firstInvalidControlName: string = FormUtils.findFirstInvalidControl(this.medHistoryForm);
    
    if (!firstInvalidControlName) {
      return;
    }
    
    let firstInvalidControl: HTMLElement = document.getElementById(firstInvalidControlName);
    
    if (!firstInvalidControl) {
      // Try to find the element by name attribute or formControlName
      const byName = document.querySelector(`[name="${firstInvalidControlName}"]`) as HTMLElement;
      const byFormControlName = document.querySelector(`[formControlName="${firstInvalidControlName}"]`) as HTMLElement;
      const byKeyId = document.querySelector(`[key-id="${firstInvalidControlName}"]`) as HTMLElement;
      
      firstInvalidControl = byName || byFormControlName || byKeyId;
      
      if (!firstInvalidControl) {
        // If it's a nested field like "mq-10100-onsetDate", try to find the parent checkbox
        // Extract the base key (e.g., "mq-10100" from "mq-10100-onsetDate")
        const baseKey = firstInvalidControlName.split('-').slice(0, 3).join('-'); // "mq-10100"
        
        const parentElement = document.getElementById(baseKey) || 
                            document.querySelector(`[key-id="${baseKey}"]`) as HTMLElement ||
                            document.querySelector(`[formControlName="${baseKey}"]`) as HTMLElement;
        
        if (parentElement) {
          firstInvalidControl = parentElement;
        } else {
          // Last resort: find any visible error message and scroll to it
          const errorElement = document.querySelector('.ng-invalid.ng-touched, [slot="error"]:not(:empty), .error-message:not(:empty)') as HTMLElement;
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      }
    }
    
    if (firstInvalidControl) {
      FormUtils.scrollToFirstInvalidControl(firstInvalidControl);
    }
  }


 trimTreatmentValues(obj: any): void {
  Object.keys(obj).forEach(key => {
    if (key.toLowerCase().includes('treatment')) {
      const val = obj[key];
      if (typeof val === 'string') {
        obj[key] = val.replace(/^[;:"@#$%^&*()_+!>?<}{|]+/, '');
      }
    }
  });
}

trimConditiontValues(obj: any): void {
  Object.keys(obj).forEach(key => {
    if (key.toLowerCase().includes('condition')) {
      const val = obj[key];
      if (typeof val === 'string') {
        obj[key] = val.replace(/^[;:"@#$%^&*()_+!>?<}{|]+/, '');
      }
    }
  });
}


  async updateMedicalHistory(spinnerStartTime: number){
    let resp = new PartialSaveResp();
    
    this.trimTreatmentValues(this.userSessionService.data.mhsApplication.medicalHistory);
    this.trimConditiontValues(this.userSessionService.data.mhsApplication.medicalHistory)
    //console.log(this.userSessionService.data.mhsApplication.medicalHistory);
    try {
      const result = await this.applicationService.updateMedicalHistoryWithExStreamCloudApplication(this.userSessionService.data.mhsId, this.userSessionService.data.mhsApplication.medicalHistory, this.userSessionService.data.isGenericDynamicForm);
      resp.httpStatusCode = result.httpStatusCode;
      resp.message = result.message;
      resp.data = result.data;
      // 201 (Created) and 304 (Not Modified) are both successful responses
      if(resp.httpStatusCode === 201 || resp.httpStatusCode === 304){
        this.appInsightsLoggerService.log('info', 'Medical history updated successfully. mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        this.submitted = true;
        this.val.emit(this.submitted);
        this.formSubmitted = false;
        this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        
        // In edit mode, navigate back to review instead of advancing to next step
        if (this.isEditFromReview) {
          this.isSavingEdit = false;
          this.formSubmitted = false;
          this.cdr.detectChanges();
          this.navigateBackToReviewAfterSave();
        }
      } else {
        this.submitted = false;
        this.isSavingEdit = false;
        this.appInsightsLoggerService.log('error', 'Medical history update failed. mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        this.formSubmitted = false;
        this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } catch (error) {
      resp.httpStatusCode = error.error.httpStatusCode;
      resp.message = error.message;
      resp.data = error.error.data;
      this.findInvalidControls(resp.data);
      this.submitted = false;
      this.appInsightsLoggerService.log('error', 'Medical history update failed. mhsId/caseId: ' + this.userSessionService.data.mhsId, error);
      this.formSubmitted = false;
      this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'Medical History Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    }
  }

  findInvalidControls(resp: PartialSaveData){
    for(const key in resp){
      const controlName = key.split('.').slice(2).join('.');
      if(controlName != null || controlName != undefined || controlName != ''){
        const control = this.medHistoryForm.get(controlName);
        control?.setErrors({ pattern: resp[key] });
      }
    }
    this.scrollToFirstInvalidControl();
  }

  public setPageTitle(){
    if(this.userSessionService.data.applicantType){
      if(this.userSessionService.data.applicantType.toLowerCase() === ApplicantType.EMPLOYEE.toLowerCase()){
        this.pageTitle = "Medical History (Employee)";
      } else if(this.userSessionService.data.applicantType.toLowerCase() === ApplicantType.SPOUSE.toLowerCase()){
        this.pageTitle = "Medical History (Spouse)";
      } else if(this.userSessionService.data.applicantType.toLowerCase() === ApplicantType.CHILD.toLowerCase()){
        this.pageTitle = "Medical History (Child)";
        if(this.isGenericDynamicForm){
          this.noHeightAndWeight = true;
        }
      } 
    }
  }

  logYesNoRadioChangeEvent(key: string, controlName: string){
    this.medHistoryForm.get(key).setValue(controlName);
  }

  public setPhysicianInfoFlag(){
    // CRITICAL: Reset flag to false at the start so it reflects current state
    // This ensures if user changes from "yes" to "no", the flag is properly updated
    this.userSessionService.data.answeredYesToRequiredPhysicianQuestion = false;
    
    this.getAllQuestionsWithPhysicianInfo();

    for(let question in this.userSessionService.data.mhsAnswers){
      let answer: string = this.userSessionService.data.mhsAnswers[question];
      if(answer == 'yes'){
        if(this.questionsWithPhysicianInfo.includes(question)){
          this.userSessionService.data.answeredYesToRequiredPhysicianQuestion = true;
          break;
        }
      }
      //Chronic/Acute
      if(question == 'mq-10007-condition' || question == 'mq-10009-condition'){
        if(answer == 'acute'){
          this.userSessionService.data.answeredYesToRequiredPhysicianQuestion = true;
          break;
        }
      }
    }
  }

  public getAllQuestionsWithPhysicianInfo() {
    for (let x in this.userSessionService.data.newMhsQuestions) {
      if (x == 'levelOneQuestions') {
        for (let y in this.userSessionService.data.newMhsQuestions[x]) {
          if (this.userSessionService.data.newMhsQuestions[x][y].additionalDetailsHasPhysicianInfo) {
            this.addToQuestionsWithPhysicianInfo(this.userSessionService.data.newMhsQuestions[x][y].key);
          }

          if(this.userSessionService.data.newMhsQuestions[x][y].additionalQuestions != undefined){
            if (this.userSessionService.data.newMhsQuestions[x][y].additionalQuestions.length > 0) {
              let additionalQuestions: AdditionalQuestion[];
              additionalQuestions = this.userSessionService.data.newMhsQuestions[x][y].additionalQuestions;
  
              for (let addQ in additionalQuestions) {
                if(additionalQuestions[addQ].levelTwoQuestions.length != undefined){
                  if (additionalQuestions[addQ].levelTwoQuestions.length > 0) {
                    let levelTwoQuestions: LevelTwoQuestion[];
                    levelTwoQuestions = additionalQuestions[addQ].levelTwoQuestions;
                    for (let z in levelTwoQuestions) {
                      if (levelTwoQuestions[z].additionalDetailsHasPhysicianInfo) {
                        this.addToQuestionsWithPhysicianInfo(levelTwoQuestions[z].key);
                      }
                    }
                  }
                }
  
                if (additionalQuestions[addQ].additionalDetailsHasPhysicianInfo) {
                  this.addToQuestionsWithPhysicianInfo(additionalQuestions[addQ].key);
                }
              }
            } else if(this.userSessionService.data.newMhsQuestions[x][y].levelTwoQuestions != undefined){
              if (this.userSessionService.data.newMhsQuestions[x][y].levelTwoQuestions.length > 0) {
                let outerLevelTwoQuestions: LevelTwoQuestion[];
                outerLevelTwoQuestions = this.userSessionService.data.newMhsQuestions[x][y].levelTwoQuestions;
                for (let q in outerLevelTwoQuestions) {
                  if (outerLevelTwoQuestions[q].additionalDetailsHasPhysicianInfo) {
                    this.addToQuestionsWithPhysicianInfo(outerLevelTwoQuestions[q].key);
                  }
                }
              }
            } 
          }
        }
      }
    }
  }

  public addToQuestionsWithPhysicianInfo(key: string){
    if(this.questionsWithPhysicianInfo == undefined){
      let physicianRequiredQuestions: string[] = [];
      physicianRequiredQuestions.push(key);
      this.questionsWithPhysicianInfo = physicianRequiredQuestions;
    } else {
      if(this.questionsWithPhysicianInfo.includes(key)){
        //do nothing
      } else {
        let physicianRequiredQuestions = this.questionsWithPhysicianInfo;
        physicianRequiredQuestions.push(key);
        this.questionsWithPhysicianInfo = physicianRequiredQuestions;
      }
    }
  }

  heightLeaveEvent(event){
    const inputValue = event.target.value;
    this.medHistoryForm.get('mq-10001-height-feet').markAsTouched();
    this.medHistoryForm.controls["mq-10001-height-feet"].setValue(inputValue.toString().trim());
    this.medHistoryForm.updateValueAndValidity();
  }

  inchesLeaveEvent(event){
    const inputValue = event.target.value;
    this.medHistoryForm.get('mq-10001-height-inches').markAsTouched();
    this.medHistoryForm.controls["mq-10001-height-inches"].setValue(inputValue.toString().trim());
    this.medHistoryForm.updateValueAndValidity();
  }

  weightLeaveEvent(event){
    const inputValue = event.target.value;
    this.medHistoryForm.get('mq-10001-weight').markAsTouched();
    this.medHistoryForm.controls["mq-10001-weight"].setValue(inputValue.toString().trim());
    this.medHistoryForm.updateValueAndValidity();
  }

  private replaceBoldTags(text: string): string {
    return text.replace(/<b>/g, '').replace(/<\/b>/g, '');
  }

  public setAnswers(){
    const medicalHistory = this.userSessionService.data.mhsApplication.medicalHistory;
    const levelOneQuestions = this.userSessionService.data.newMhsQuestions.levelOneQuestions;

    // Accumulate final results in the finalData object.
    const finalData: {[key: string]: string} = {};

    // Create a map to hold diagnosis info from additionalQuestions
    const uiDiagnosisMap = new Map<string, string>();

    // Process ui-based questions
    const uiQuestions = levelOneQuestions.filter((q:any) => q.use === 'ui');
    uiQuestions.forEach((uiQ: any) => {
      // Determine processing based on applicantType
      if(uiQ.applicantType && uiQ.applicantType.includes('child')) {
        // For child, process the entire levelOneQuestion as  a group
        const mergedLabels: string[] = [];
        if(Array.isArray(uiQ.additionalQuestions)) {
          uiQ.additionalQuestions.forEach((addQ: any) => {
            const hasLevelTwo = Array.isArray(addQ.levelTwoQuestions) && addQ.levelTwoQuestions.length > 0;
            if(hasLevelTwo){
              // Only use levelTwoQuestions answered "yes"
              addQ.levelTwoQuestions.forEach((levelTwoQ: any) => {
                const subAnswer = medicalHistory[levelTwoQ.key];
                if(subAnswer && subAnswer.toLowerCase() === 'yes') {
                  mergedLabels.push(levelTwoQ.label.replace(/\?/g, ''));
                }
              });
            } else {
              // If there are no levelTwoQuestions , then use the additional question's own answer
              const answer = medicalHistory[addQ.key];
              if( answer && answer.toLowerCase() === 'yes') {
                mergedLabels.push(addQ.label.replace(/\?/g, ''));
              }
            }
          });
        }
        if(Array.isArray(uiQ.levelTwoQuestions)) {
          uiQ.levelTwoQuestions.forEach((levelTwoQ: any) => {
            const subAnswer = medicalHistory[levelTwoQ.key];
            if(subAnswer && subAnswer.toLowerCase() === 'yes') {
              mergedLabels.push(levelTwoQ.label.replace(/\?/g, ''));
            }
          });

        }
        if (mergedLabels.length) {
          const mergedAnswer = mergedLabels.join('| ');
          // Mark the parent ui question as "yes"
          finalData[uiQ.key] = 'yes';
          // Assign to it's diagnosis field (assuming additionalDetails contains a key with "-diagnosis")
          if( Array.isArray(uiQ.additionalDetails)) {
            const diagnosisField = uiQ.additionalDetails.find((d: any) => d.key && d.key.includes('-diagnosis'));
            if(diagnosisField) {
              finalData[diagnosisField.key] = this.replaceBoldTags(mergedAnswer);
            }
          }
          // Also add to the ui diagnosis map for later mapping to mhs questions (using uiQ.key)
          uiDiagnosisMap.set(uiQ.key, this.replaceBoldTags(mergedAnswer));
        } else {
          // Mark the parent ui question as "no"
          finalData[uiQ.key] = 'no';
        }
      }
      else if (uiQ.applicantType && (uiQ.applicantType.includes('employee') || uiQ.applicantType.includes('spouse'))) {
        // For employee/spouse applicants, process each additional question separately.
        if (Array.isArray(uiQ.additionalQuestions)) {
          uiQ.additionalQuestions.forEach((addQ: any) => {
            // Check if this additional question is answered "yes"
            const answer = medicalHistory[addQ.key];
            if (answer && answer.toLowerCase() === 'yes') {
              // Mark the additional question's key as yes
              finalData[addQ.key] = 'yes';
              let diagResult = '';
              if (Array.isArray(addQ.levelTwoQuestions) && addQ.levelTwoQuestions.length > 0) {
                const levelTwoLabels: string[] = [];
                addQ.levelTwoQuestions.forEach((levelTwoQ: any) => {
                  const subAnswer = medicalHistory[levelTwoQ.key];
                  if (subAnswer && subAnswer.toLowerCase() === 'yes') {
                    levelTwoLabels.push(levelTwoQ.label.replace(/\?/g, ''));
                  }
                });
                diagResult = levelTwoLabels.join('| ');
              } else {
                // If no levelTwoQuestions, use the additional question's label
                diagResult = addQ.label;
              }
              // Save the diagnosis for this additional question
              finalData[`${addQ.key}-diagnosis`] = this.replaceBoldTags(diagResult);
              // Also record this in the ui diagnosis map, using the additional question's key
              uiDiagnosisMap.set(addQ.key, this.replaceBoldTags(diagResult));
            }
          });
        }
      }
    });

    // Process mhs based questions
    const mhsQuestions = levelOneQuestions.filter((q: any) => q.use === 'mhs');
    mhsQuestions.forEach((mhsQ: any) => {
      // Use the provided key to  look up the corresponding ui  diagnosis info.
      const uiKey = mhsQ.key;
      const diagnosis = uiDiagnosisMap.get(uiKey);

      if(diagnosis) {
        // Mark the mhs questions as "yes"
        finalData[mhsQ.key] = 'yes';
        // Use it's additionalDetails to assign the diagnosis string.
        if(Array.isArray(mhsQ.additionalDetails)) {
          const diagnosisField = mhsQ.additionalDetails.find((d: any) => d.key && d.key.includes('-diagnosis'));
          if(diagnosisField){
            finalData[diagnosisField.key] = this.replaceBoldTags(diagnosis);
          }
        }
      }

    });

    // Add status (resolved/ongoing) based on whether treatment info exists
    const medHistory = this.userSessionService.data.mhsApplication.medicalHistory;
    Object.entries(medHistory).forEach(([key, value]) => {
      if (key.endsWith('-onsetDate') && value && value.toString().trim().length > 0) {
        const baseKey = key.replace('-onsetDate', '');
        const resolutionDateKey = `${baseKey}-resolutionDate`;
        const statusKey = `${baseKey}-status`;
        
        const hasResolutionDate = medHistory.hasOwnProperty(resolutionDateKey) && medHistory[resolutionDateKey].toString().trim().length > 0;
        this.mhsService.AddAnswerToMhsMedicalHistory(statusKey, hasResolutionDate ? 'resolved': 'ongoing');
      }
    });

    // Merge finalData into medicalHistory (overwrite if already present)
    Object.entries(finalData).forEach(([key, value]) => {
      this.mhsService.AddAnswerToMhsMedicalHistory(key, value);
    });

    // Cleanup medicalHistory for answered/merged questions
    levelOneQuestions.forEach((q: any) => {
      const applicantTypes = q.applicantType || [];

      // child specific cleanup: remove additionalQuestion keys of merged into finalData
      if (applicantTypes.includes('child') && Array.isArray(q.additionalQuestions)) {
        q.additionalQuestions.forEach((addQ: any) => {
          if( finalData.hasOwnProperty(q.key)) {
            // remove the additionalQuestion answer
           delete this.userSessionService.data.mhsApplication.medicalHistory[addQ.key];

           // Also remove level two question keys if present
           if (Array.isArray(addQ.levelTwoQuestions)) {
            addQ.levelTwoQuestions.forEach((lvl2Q: any) => {
              delete this.userSessionService.data.mhsApplication.medicalHistory[lvl2Q.key];
            });
           }
          }
        });
      }

      // Employee/Spouse specific cleanup: remove only levelTwoQuestion keys that were merged
      if ((applicantTypes.includes('employee') || applicantTypes.includes('spouse')) && Array.isArray(q.additionalQuestions)) {
        q.additionalQuestions.forEach((addQ: any) => {
          if (finalData.hasOwnProperty(`${addQ.key}-diagnosis`)) {
            if (Array.isArray(addQ.levelTwoQuestions)) {
              addQ.levelTwoQuestions.forEach((lvl2Q: any) => {
                const hasAdditionalDetails = Array.isArray(lvl2Q.additionalDetails) && lvl2Q.additionalDetails.length > 0;
                if(hasAdditionalDetails){
                  delete this.userSessionService.data.mhsApplication.medicalHistory[`${addQ.key}-diagnosis`];
                }
                let answer: string = this.userSessionService.data.mhsAnswers[lvl2Q.key];
                if( answer === 'yes' && (q.key === 'qg-10098') || (q.key === 'qg-10151') ){
                  //do nothing, we need the 'yes' answer in the payload
                } else {
                  delete this.userSessionService.data.mhsApplication.medicalHistory[lvl2Q.key];
                }
              });
            }
          }
        });
      }
    });
  }

  public gatherAnswers(){
    if(this.userSessionService.data.mhsAnswers){
      delete this.userSessionService.data.mhsAnswers;
    }
    if(this.userSessionService.data.mhsApplication.medicalHistory){
      delete this.userSessionService.data.mhsApplication.medicalHistory;
    }

    if(this.checkIfQuestions4And6NeedToBeReindexed()){
      this.fillInNewControlNames();
    }

    //Iterate over all the keys in the form
    Object.keys(this.medHistoryForm.controls).forEach(controlName => {
      // Use the key to get the value
      let controlValue = this.medHistoryForm.controls[controlName].value;

      if((this.medHistoryForm.get(controlName).value).constructor.name === "Object"){
         // Is the control a month picker date?
        if(controlName.indexOf('Date') > -1){  
          
          //dates are in a new format
          //intentionally left blank for now
  
        } else {
          // Iterate over non-date objects (e.g. checkboxes) and modify or exclude 
          Object.entries(controlValue).forEach(checkbox => {
            if(checkbox[1] === true){
              this.mhsService.AddAnswerToMhsMedicalHistory(checkbox[0], "yes");
            } else if(checkbox[1] === false){
              // these are unchecked checkboxes.  do not add to payload
            } else if(checkbox[1] == 'acute' || checkbox[1] == 'chronic'){
              this.mhsService.AddAnswerToMhsMedicalHistory(checkbox[0], checkbox[1].toString());
            }
          })
        }
        
      }  else {
          let useThisControlName = this.checkIfControlNameNeedsToSwap(controlName);
          if(!this.checkIfMonthsOrYears(controlName)){
            this.mhsService.AddAnswerToMhsMedicalHistory(useThisControlName, controlValue);
          }
      }

    });
  } 

  checkIfMonthsOrYears(controlName){
    if(controlName.slice(-5) === "-year"){
      return true;
    } else if(controlName.slice(-6) === "-month"){
      return true;
    }
    return false;
  }

  checkIfControlNameNeedsToSwap(controlName){

    for(let rc of this.renamedControls){
      if(rc.originalName === controlName){
        return rc.newName;
      }
    }

    return controlName;
  }

  checkIfQuestions4And6NeedToBeReindexed(){
    let bothParts: string[];

    Object.keys(this.medHistoryForm.controls).forEach(controlName => {

      if(controlName.includes('_')){
        bothParts = controlName.split("_");

        if(!this.allIndexes.includes(Array.from(bothParts[1])[0])){
          this.allIndexes.push(Array.from(bothParts[1])[0]);
        }
      }
    
    });

    this.allIndexes.sort();

    if(Number(this.allIndexes[this.allIndexes.length-1]) > this.allIndexes.length){
      return true;
    }

    this.allIndexes = [] as string[];
    return false;
  }

  fillInNewControlNames(){
    let compareIndex: number = 1;

    for(let n of this.allIndexes){
      if(Number(n) != compareIndex){

        let indexToReplace = new IndexCompare();
        indexToReplace.originalIndex = n;
        indexToReplace.newIndex = compareIndex.toString();
        this.indexesToReplace.push(indexToReplace);
      }
      compareIndex++;
    }

    let bothParts: string[];

    Object.keys(this.medHistoryForm.controls).forEach(controlName => {

      if(controlName.includes('_')){
        bothParts = controlName.split("_");

        let newIndex = this.potentialNewIndex(Array.from(bothParts[1])[0]);
        if(Number(newIndex) > 0){
          let renamedControl = new RenamedControl();
          renamedControl.originalName = controlName;
          renamedControl.newName = bothParts[0] + "_" + newIndex + bothParts[1].slice(1);
          this.renamedControls.push(renamedControl);
        }
      }
    
    });
  }

  public checkApplicantType(type: string, formTypes: string[]): boolean {
    for (let formType of formTypes) {
      if (type.toLowerCase() === formType.toLowerCase()) {
        return true;
      }
    }
    return false;
  }

  potentialNewIndex(indexOfControl: string){

    for(let p of this.indexesToReplace){
      if(p.originalIndex === indexOfControl){
        return p.newIndex;
      }
    }
    return -1;
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from MedHistory.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      'prism-ui-modal-cancel-modal'
    );

    if (modal && event.target === modal) {
      this.onCancelModal('prism-ui-modal-cancel-modal');
    }
  }

  onCancel(){
    this.cancelEmitter.emit();
  }

  onBack(){
    this.backEmitter.emit();
  }

  canDeactivate(){
    if (!this.submitted && !this.userSessionService.data.sessionEnded){
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  /**
   * Edit-mode "Back" button handler. Marks the navigation as safe and returns
   * to /review without saving any pending changes.
   */
  goBack(): void {
    if (this.isSavingEdit) {
      return;
    }
    // Discard any unsaved changes — do not overwrite the session cache.
    // Restore the snapshot taken at the start of the edit session so the user
    // sees the original data if they re-open the page from review.
    this.discardFormDataOnDestroy = true;
    if (this.editSessionSnapshot) {
      this.userSessionService.data['medHistoryFormData'] = this.editSessionSnapshot;
    } else {
      delete this.userSessionService.data['medHistoryFormData'];
    }
    // Mark as a safe navigation so the cancel-modal / leave-anyway flow is
    // bypassed when the deactivate guard fires.
    this.submitted = true;
    this.router.navigate([EEOI_APP_ROUTES.AUTH_ELEC_SIGN]);
  }

  /**
   * Edit-mode "Save" button handler. Validates the form and, if valid,
   * fills the medical history model, persists it to the backend, and navigates
   * back to /review with the success toast.
   */
  saveAndGoToReview(): void {
    // Defend against a double-click while a save is already in flight.
    if (this.isSavingEdit) {
      return;
    }

    // Before validating, ensure any unchecked checkbox fields are removed
    this.cleanupUncheckedCheckboxFields();
    
    // CRITICAL: Force save immediately after cleanup to capture the cleaned state
    this.saveFormData(true);
    
    this.medHistoryForm.markAllAsTouched();
    
    // Trigger change detection to ensure error messages appear immediately
    this.cdr.detectChanges();

    if (!this.medHistoryForm.valid) {
      this.scrollToFirstInvalidControl();
      return;
    }

    // Set saving state BEFORE calling save to show button spinner
    this.isSavingEdit = true;
    this.cdr.detectChanges();
    
    const spinnerStartTime = new Date().getTime();
    
    this.appInsightsLoggerService.log('info', 'MedHistoryComponent: Edit-mode save started.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');

    // Push the form values into the session-level medical history model
    this.gatherAnswers();
    this.userSessionService.data.mhsApplication.companyId = this.userSessionService.data.companyId;
    this.userSessionService.data.mhsApplication.formNumber = this.userSessionService.data.formNumber;

    this.setPhysicianInfoFlag();

    if(this.isGenericDynamicForm){
      this.setAnswers();
    }
    
    // Save to backend
    this.savePartialApplication(spinnerStartTime);
  }

  /**
   * Saves medical history data in edit mode. Separate from updateMedicalHistory
   * to handle the edit-mode navigation flow.
   */
  async savePartialApplication(spinnerStartTime: number) {
    let resp = new PartialSaveResp();
    
    this.trimTreatmentValues(this.userSessionService.data.mhsApplication.medicalHistory);
    this.trimConditiontValues(this.userSessionService.data.mhsApplication.medicalHistory);
    
    try {
      const result = await this.applicationService.updateMedicalHistoryWithExStreamCloudApplication(
        this.userSessionService.data.mhsId,
        this.userSessionService.data.mhsApplication.medicalHistory,
        this.userSessionService.data.isGenericDynamicForm
      );
      resp = result;
      this.appInsightsLoggerService.log('info', 'Partial application saved successfully. Component: MedHistoryComponent' + ' mhsId/caseId: ' + result.data.mhsId, '');
      
      // 201 (Created) and 304 (Not Modified) are both successful responses
      if (resp.httpStatusCode === 201 || resp.httpStatusCode === 304) {
        this.submitted = true;
        this.isSavingEdit = false;
        this.formSubmitted = false;
        this.cdr.detectChanges();
        this.navigateBackToReviewAfterSave();
        
        this.appInsightsLoggerService.log('info', 'MedHistoryComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'MedHistoryComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      } else {
        this.appInsightsLoggerService.log('error', 'Medical history updated with error. mhsId/caseId: ' + resp.data.mhsId, '');
        this.submitted = false;
        this.formSubmitted = false;
        this.isSavingEdit = false;
        this.cdr.detectChanges();
        this.appInsightsLoggerService.log('info', 'MedHistoryComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'MedHistoryComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } catch (error) {
      this.appInsightsLoggerService.log('error', 'Medical history edit-mode save failed: ' + error, '');
      this.submitted = false;
      this.formSubmitted = false;
      this.isSavingEdit = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Common post-save navigation: flip the success-toast flag and route back
   * to /review. Mirrors the applicant-info and employee-info pages so
   * the user sees the same success banner regardless of which section they edited.
   */
  private navigateBackToReviewAfterSave(): void {
    this.userSessionService.data['showCoverageSavedToast'] = true;
    sessionStorage.setItem('showCoverageSavedToast', 'true');

    const destinationRoute = EEOI_APP_ROUTES.AUTH_ELEC_SIGN;
    this.submitted = true;
    this.router.navigate([destinationRoute], { state: { showSuccess: true } });
    this.appInsightsApiService.startNavigationEvent(destinationRoute.toString());
  }
}